/** A slot counts as in view when at least half of it is in the viewport, the area threshold Active View uses, so numbers stay comparable. Not a refresh-policy requirement. */
const AD_VIEWPORT_MIN_VISIBLE_RATIO = 0.5

export type ViewportTracker = {
  isInViewport: () => boolean | undefined
  dispose: () => void
}

const isCoveredByFullscreen = (element: Element) => {
  const fullscreenElement = document.fullscreenElement

  return !!fullscreenElement && !fullscreenElement.contains(element)
}

export const createViewportTracker = (
  element: Element | null,
  onChange?: (isInViewport: boolean) => void,
): ViewportTracker => {
  let isIntersecting: boolean | undefined
  let lastReported: boolean | undefined

  if (element === null || typeof IntersectionObserver === 'undefined') {
    return { isInViewport: () => undefined, dispose: () => {} }
  }

  const readInViewport = () =>
    isIntersecting === undefined ? undefined : isIntersecting && !isCoveredByFullscreen(element)

  const notifyIfChanged = () => {
    const next = readInViewport()

    if (next === undefined || next === lastReported) return

    lastReported = next
    onChange?.(next)
  }

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[entries.length - 1]

      if (!entry) return

      isIntersecting = entry.intersectionRatio >= AD_VIEWPORT_MIN_VISIBLE_RATIO
      notifyIfChanged()
    },
    { threshold: AD_VIEWPORT_MIN_VISIBLE_RATIO },
  )

  observer.observe(element)
  document.addEventListener('fullscreenchange', notifyIfChanged)

  return {
    isInViewport: readInViewport,
    dispose: () => {
      observer.disconnect()
      document.removeEventListener('fullscreenchange', notifyIfChanged)
    },
  }
}
